import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
// import { useMatomo } from '../contexts/SimpleMatomoContext';
import LanguageSelector from '../components/LanguageSelector';
import { usePWA } from '../components/PWAProvider';
import toast from 'react-hot-toast';
import { apiUrl, API_ORIGIN } from '../utils/apiBase';

const LANDING_PAGE_URL = 'https://salesign.app';

const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { isInstalled } = usePWA();
  // const { trackCustomEvent } = useMatomo();
  const trackCustomEvent = () => {}; // Mock function
  const [propertyCount, setPropertyCount] = useState(0);
  const [searchRequestCount, setSearchRequestCount] = useState(0);
  const [loadingCount, setLoadingCount] = useState(true);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [backgroundImageLoaded, setBackgroundImageLoaded] = useState(false);

  useEffect(() => {
    // Fetch property count and background image
    const fetchData = async () => {
      try {
        const normalizeBgUrl = (value) => {
          if (!value) return value;
          const v = String(value).trim();
          if (/^https?:\/\//i.test(v)) return v;
          // If backend returns relative (e.g. "/api/media/..."), ensure it points to API host
          if (v.startsWith('/')) return `${API_ORIGIN}${v}`;
          return `${API_ORIGIN}/${v}`;
        };

        // Fetch property count (use total from paginated response, not first page length)
        const propertiesResponse = await fetch(apiUrl('/properties') + '?per_page=1');
        if (propertiesResponse.ok) {
          const data = await propertiesResponse.json();
          const total = data.total != null ? data.total : (data.data?.length ?? 0);
          setPropertyCount(total);
        }

        // Fetch active search request count
        const searchResponse = await fetch(apiUrl('/area-preferences/active-count'));
        if (searchResponse.ok) {
          const data = await searchResponse.json();
          setSearchRequestCount(data.data?.count ?? 0);
        }

        // Fetch active background image
        const bgResponse = await fetch(apiUrl('/background-image/active'));
        if (bgResponse.ok) {
          const bgData = await bgResponse.json();
          if (bgData.success && bgData.data) {
            const normalizedUrl = normalizeBgUrl(bgData.data.url);
            // Preload the image before setting it
            const img = new Image();
            img.onload = () => {
              setBackgroundImage({ ...bgData.data, url: normalizedUrl });
              setBackgroundImageLoaded(true);
            };
            img.onerror = () => {
              // If image fails to load, still set it but mark as loaded to avoid infinite loading
              setBackgroundImage({ ...bgData.data, url: normalizedUrl });
              setBackgroundImageLoaded(true);
            };
            img.src = normalizedUrl;
          } else {
            setBackgroundImageLoaded(true); // No background image, use default
          }
        } else {
          setBackgroundImageLoaded(true); // API failed, use default
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setBackgroundImageLoaded(true); // Error, use default
      } finally {
        setLoadingCount(false);
      }
    };

    fetchData();
  }, []);

  const handleInstallPWA = async () => {
    const promptEvent = window.deferredPrompt;
    if (promptEvent) {
      try {
        promptEvent.prompt();
        const { outcome } = await promptEvent.userChoice;
        
        // Track PWA installation attempt
        trackCustomEvent('PWA', 'Install Attempt', outcome);
        
        if (outcome === 'accepted') {
          toast.success('PWA installed successfully!');
          trackCustomEvent('PWA', 'Install Success');
        } else {
          toast('PWA installation cancelled', { icon: 'ℹ️' });
          trackCustomEvent('PWA', 'Install Rejected');
        }

        window.deferredPrompt = null;
      } catch (error) {
        console.error('PWA installation error:', error);
        toast.error('PWA installation failed');
      }
    } else {
      // Check if already installed
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isInStandaloneMode = ('standalone' in window.navigator) && window.navigator.standalone;
      
      if (isStandalone || (isIOS && isInStandaloneMode)) {
        toast('PWA is already installed!', { icon: 'ℹ️' });
      } else {
        toast.error('PWA install prompt not available yet. On Android Chrome: Menu → Add to Home screen.');
        trackCustomEvent('PWA', 'Install Not Available');
      }
    }
  };

  const handleReportProperty = () => {
    if (isAuthenticated && user?.role === 'land_reporter') {
      // User is already authenticated as land_reporter, go directly to report page
      navigate('/report');
    } else if (isAuthenticated && user?.role === 'land_seeker') {
      // User is authenticated but as land_seeker, show error
      toast.error('You are registered as a Land Seeker. You cannot report properties. Please contact support if you need to change your role.');
    } else {
      // User is not authenticated, go to login with land_reporter role pre-selected
      navigate('/login', { state: { role: 'land_reporter' } });
    }
  };

  const handleSearchProperty = () => {
    if (isAuthenticated && user?.role === 'land_seeker') {
      // User is already authenticated as land_seeker, go directly to land search
      navigate('/land-search');
    } else if (isAuthenticated && user?.role === 'land_reporter') {
      // User is authenticated but as land_reporter, show error
      toast.error('You are registered as a Land Reporter. You cannot search properties. Please contact support if you need to change your role.');
    } else {
      // User is not authenticated, go to land search flow (which will handle registration)
      navigate('/land-search');
    }
  };

  const handleSubscription = () => {
    if (isAuthenticated && user?.role === 'land_seeker') {
      navigate('/subscription');
    } else if (isAuthenticated && user?.role === 'land_reporter') {
      toast.error('You are registered as a Land Reporter. You cannot manage subscriptions. Please contact support if you need to change your role.');
    } else {
      navigate('/login', { state: { role: 'land_seeker' } });
    }
  };

  const handlePreview = () => {
    if (isAuthenticated && user?.role === 'land_seeker') {
      navigate('/preview');
    } else if (isAuthenticated && user?.role === 'land_reporter') {
      toast.error('You are registered as a Land Reporter. You cannot preview properties. Please contact support if you need to change your role.');
    } else {
      navigate('/login', { state: { role: 'land_seeker' } });
    }
  };

  return (
    <div className="min-h-screen bg-salesign-gray flex flex-col relative">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-blue-100 via-green-50 to-blue-200 opacity-20 transition-opacity duration-500"
        style={{
          backgroundImage: backgroundImage && backgroundImageLoaded
            ? `url('${backgroundImage.url}')`
            : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: backgroundImage && backgroundImageLoaded ? 0.2 : 0.1
        }}
      ></div>
      
      {/* Content overlay */}
      <div className="relative z-10 flex flex-col h-full">
      {/* Header */}
      <header className="p-4 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <img
            src="/logo512.svg"
            alt="SaleSign"
            className="w-8 h-8 sm:w-9 sm:h-9"
            loading="eager"
            decoding="async"
          />
          <h1 className="text-2xl font-bold text-salesign-black font-geist">
            {t('app.name')}
          </h1>
        </div>
        <LanguageSelector />
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4">
        <div className="text-center mb-8">
          <p className="text-lg text-gray-600 mb-4 font-geist">
            {t('home.subtitle')}
          </p>
          
          {/* Property Counter */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-4 max-w-sm mx-auto">
            <div className="flex items-center justify-center space-x-2">
              <span className="text-2xl">🏠</span>
              <div className="text-center">
                <div className="text-2xl font-bold text-salesign-black font-geist">
                  {loadingCount ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  ) : (
                    propertyCount.toLocaleString()
                  )}
                </div>
                <div className="text-sm text-gray-600 font-geist">
                  {t('home.propertiesAvailable')}
                </div>
              </div>
            </div>
          </div>

          {/* Search Requests Counter */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-8 max-w-sm mx-auto">
            <div className="flex items-center justify-center space-x-2">
              <span className="text-2xl">🔍</span>
              <div className="text-center">
                <div className="text-2xl font-bold text-salesign-black font-geist">
                  {loadingCount ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  ) : (
                    searchRequestCount.toLocaleString()
                  )}
                </div>
                <div className="text-sm text-gray-600 font-geist">
                  {t('home.searchRequests')}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-4 w-full max-w-md">
          {/* Report Property is only for guests + land reporters (not land seekers) */}
          {(!isAuthenticated || user?.role === 'land_reporter') && (
            <button
              onClick={handleReportProperty}
              className="w-full btn-secondary text-lg py-4 font-geist"
            >
              {isAuthenticated && user?.role === 'land_reporter'
                ? `🏗️ ${t('home.reportPropertyContinue')}`
                : t('home.reportProperty')
              }
            </button>
          )}

          {/* Search Property is only for guests + land seekers (not land reporters) */}
          {(!isAuthenticated || user?.role === 'land_seeker') && (
            <button
              onClick={handleSearchProperty}
              className="w-full btn-secondary text-lg py-4 font-geist"
            >
              {isAuthenticated && user?.role === 'land_seeker'
                ? `🔍 ${t('home.searchPropertyContinue')}`
                : t('home.searchProperty')
              }
            </button>
          )}

          {/* For guests: show PWA install CTA where Login/Register used to be */}
          {!isAuthenticated && !isInstalled && (
            <button
              onClick={handleInstallPWA}
              className="w-full btn-primary text-lg py-4 font-geist"
            >
              {t('home.installPWA')}
            </button>
          )}

          {isAuthenticated && user?.role === 'land_seeker' && (
            <>
              {!user?.has_active_subscription && (
                <button
                  onClick={handlePreview}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors text-lg font-geist"
                >
                  👀 {t('home.browsePreview')}
                </button>
              )}
              <button
                onClick={() => navigate('/preferences')}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors text-lg font-geist"
              >
                📧 {t('home.manageEmailPreferences')}
              </button>
              <button
                onClick={handleSubscription}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors text-lg font-geist"
              >
                💳 {t('home.manageSubscription')}
              </button>
              <button
                onClick={() => logout()}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors text-lg font-geist"
              >
                🚪 {t('navigation.logout')}
              </button>
            </>
          )}

          {isAuthenticated && user?.role === 'land_reporter' && (
            <>
              <button
                onClick={() => navigate('/reporter/dashboard')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors text-lg font-geist"
              >
                📊 {t('home.viewDashboard')}
              </button>
              <button
                onClick={() => logout()}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors text-lg font-geist"
              >
                🚪 {t('navigation.logout')}
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <footer className="mt-12 text-center space-y-3">
          <a
            href={LANDING_PAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 hover:border-gray-400 transition-colors font-geist"
            aria-label={t('home.exitToLandingAria')}
          >
            {t('home.exitToLanding')}
          </a>
          <p className="text-gray-500 font-geist">
            {t('home.poweredBy')}{' '}
            <a
              href="https://ventaplaza.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {t('home.ventaplazaLink')}
            </a>
          </p>
        </footer>
      </main>
      </div>
    </div>
  );
};

export default HomePage;